import React, {useState, useEffect} from "react";
import {
    Smartphone,
    Lock,
    Eye,
    EyeOff,
    Loader,
    CheckCircle2,
    XCircle,
    Info,
    Mail,
    Store
} from "lucide-react";
import {api} from "../../config/api";
import secureStorage from "../../utils/secureStorage";
import {useQueryClient} from '@tanstack/react-query';
import {Modal, ListLoader, inputClass} from "./SettingsUI";

// Doit correspondre à la règle de l'API (EmployeeBackofficeController)
const MIN_PASSWORD_LENGTH = 8;

const StatusBadge = ({enabled}) => (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
        enabled
            ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
    }`}>
        {enabled
            ? <><CheckCircle2 className="w-3.5 h-3.5"/>Activé</>
            : <><XCircle className="w-3.5 h-3.5"/>Désactivé</>
        }
    </span>
);

/**
 * Accès de l'employé à l'app mobile Quickcom360.
 * L'employé se connecte avec son email et le même mot de passe que le backoffice,
 * et ne voit que les points de vente auxquels il est rattaché.
 */
const Quickcom360AccessModal = ({employee, onClose, showNotification}) => {
    const queryClient = useQueryClient();

    const [status, setStatus] = useState(null);
    const [loadingStatus, setLoadingStatus] = useState(true);

    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchStatus = async () => {
            try {
                const accountId = await secureStorage.getItem("selectedAccount");
                const response = await api.get(`/employees/backoffice/${employee.id}`, {
                    headers: {'X-account-id': accountId.id, 'Accept': 'application/json'}
                });
                setStatus(response.data);
            } catch (err) {
                console.error("Erreur chargement statut Quickcom360:", err);
                setStatus({quickcom360_enabled: false, has_password: false, accounts: []});
            } finally {
                setLoadingStatus(false);
            }
        };
        fetchStatus();
    }, [employee.id]);

    const isEnabled = !!status?.quickcom360_enabled;
    const hasPassword = !!status?.has_password;
    const email = status?.email || employee.email;

    const validate = () => {
        const value = password.trim();
        if (!email) {
            setError("L'employé doit avoir un email pour se connecter à Quickcom360");
            return false;
        }
        if (!hasPassword && !value) {
            setError("Le mot de passe est requis");
            return false;
        }
        if (value && value.length < MIN_PASSWORD_LENGTH) {
            setError(`Le mot de passe doit contenir au moins ${MIN_PASSWORD_LENGTH} caractères`);
            return false;
        }
        setError(null);
        return true;
    };

    const handleToggle = async (enable) => {
        if (enable && !validate()) return;
        setSubmitting(true);
        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            const action = enable ? 'enable' : 'disable';
            const payload = enable && password.trim() ? {password: password.trim()} : {};

            const response = await api.put(`/employees/backoffice/${employee.id}/quickcom360/${action}`, payload, {
                headers: {'X-account-id': accountId.id, 'Content-Type': 'application/json'}
            });

            setStatus(response.data?.employee || {...status, quickcom360_enabled: enable});
            setPassword('');
            queryClient.invalidateQueries({queryKey: ['employees']});
            showNotification('success', enable
                ? `Accès Quickcom360 activé pour ${employee.name}`
                : `Accès Quickcom360 désactivé pour ${employee.name}`);
        } catch (err) {
            const msg = err.response?.data?.error || err.response?.data?.detail || err.response?.data?.message
                || (enable ? "Erreur lors de l'activation" : "Erreur lors de la désactivation");
            showNotification('error', msg);
        } finally {
            setSubmitting(false);
        }
    };

    const footer = !loadingStatus && (
        <div className="grid grid-cols-2 border-t border-gray-200 dark:border-gray-700">
            <button type="button" onClick={onClose} disabled={submitting}
                    className="py-4 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium disabled:opacity-50">
                Fermer
            </button>
            {isEnabled ? (
                <button type="button" onClick={() => handleToggle(false)} disabled={submitting}
                        className="py-4 font-medium border-l border-gray-200 dark:border-gray-700 text-red-600 dark:text-red-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 flex items-center justify-center gap-2">
                    {submitting ? <Loader className="w-4 h-4 animate-spin"/> : <XCircle className="w-4 h-4"/>}
                    Désactiver l'accès
                </button>
            ) : (
                <button type="button" onClick={() => handleToggle(true)} disabled={submitting || !email}
                        className="py-4 font-medium border-l border-gray-200 dark:border-gray-700 text-primary hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 flex items-center justify-center gap-2">
                    {submitting ? <Loader className="w-4 h-4 animate-spin"/> : <Smartphone className="w-4 h-4"/>}
                    Activer l'accès
                </button>
            )}
        </div>
    );

    return (
        <Modal title="Accès Quickcom360" subtitle={employee.name} onClose={onClose} footer={footer}>
            {loadingStatus ? (
                <ListLoader label="Chargement du statut…"/>
            ) : (
                <div className="space-y-4">
                    <div className="flex justify-center"><StatusBadge enabled={isEnabled}/></div>

                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-3 flex items-start gap-2">
                        <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0"/>
                        <p className="text-xs text-blue-800 dark:text-blue-300">
                            {isEnabled
                                ? "Cet employé peut se connecter à l'app Quickcom360 avec son email et son mot de passe. "
                                : "Autorisez cet employé à se connecter à l'app Quickcom360 avec son email et son mot de passe. "}
                            Il ne verra que les points de vente auxquels il est rattaché.
                            Le mot de passe est le même que celui du backoffice.
                        </p>
                    </div>

                    <div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Email de connexion :</span>
                        <div className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-200">
                            <Mail className="w-4 h-4 text-gray-400 flex-shrink-0"/>
                            {email || <span className="text-red-500">Aucun email — à renseigner sur la fiche employé</span>}
                        </div>
                    </div>

                    <div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Points de vente accessibles :</span>
                        <div className="flex flex-wrap gap-2">
                            {(status?.accounts || []).map(account => (
                                <span
                                    key={account.id}
                                    className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-full"
                                >
                                    <Store className="w-3.5 h-3.5 text-gray-400"/>
                                    {account.name}
                                </span>
                            ))}
                        </div>
                    </div>

                    {!isEnabled && (
                        <div>
                            <label htmlFor="q360-password" className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                                {hasPassword ? 'Nouveau mot de passe' : 'Mot de passe'} :
                            </label>
                            <div className="relative">
                                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"/>
                                <input
                                    id="q360-password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={e => {
                                        setPassword(e.target.value);
                                        if (error) setError(null);
                                    }}
                                    placeholder={hasPassword ? '••••••••' : `Minimum ${MIN_PASSWORD_LENGTH} caractères`}
                                    autoComplete="new-password"
                                    className={`${inputClass(error)} pl-9 pr-10`}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(v => !v)}
                                    tabIndex={-1}
                                    aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                                >
                                    {showPassword ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                                </button>
                            </div>
                            {hasPassword && (
                                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                    Laisser vide pour garder l'actuel. Le changer le modifie aussi pour le backoffice.
                                </p>
                            )}
                        </div>
                    )}
                    {error && <p className="text-xs sm:text-sm text-red-500">{error}</p>}
                </div>
            )}
        </Modal>
    );
};

export default Quickcom360AccessModal;
